import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { getApiTokens, createApiToken, deleteApiToken, getUserPreferences, updateUserPreferences, generateApiKey, getApiKeys, revokeApiKey, saveWebhookConfig, testErpWebhook } from '@/services/api';
import { toast } from 'sonner';
import { 
  Settings, 
  Key, 
  Link as LinkIcon, 
  Trash2, 
  Plus,
  CheckCircle2,
  XCircle,
  Loader2,
  User,
  Building,
  AlertCircle,
  Eye,
  EyeOff,
  SlidersHorizontal,
  Save,
  Code,
  Copy,
  RefreshCw,
  Shield,
  Tag
} from 'lucide-react';
import { motion } from 'framer-motion';

export default function SettingsPage() {
  const { user } = useAuth();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTokens, setShowTokens] = useState({});
  const [newTokens, setNewTokens] = useState({
    backmarket: '',
    pennylane: ''
  });
  const [scanLimit, setScanLimit] = useState(200);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [newApiKey, setNewApiKey] = useState(null);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [erpUrl, setErpUrl] = useState('');
  const [erpApiKey, setErpApiKey] = useState('');
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState(null);

  useEffect(() => {
    fetchTokens();
    fetchPreferences();
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const keys = await getApiKeys();
      setApiKeys(keys);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    }
  };

  const handleGenerateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Donnez un nom à la clé (ex: ERP, Test, Comptabilité)');
      return;
    }
    setGeneratingKey(true);
    try {
      const result = await generateApiKey({ name: newKeyName.trim() });
      setNewApiKey(result.api_key);
      setNewKeyName('');
      await fetchApiKeys();
      toast.success(`Clé API "${result.name}" générée`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      await revokeApiKey(keyId);
      await fetchApiKeys();
      toast.success('Clé révoquée');
    } catch {
      toast.error('Erreur');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  const fetchPreferences = async () => {
    try {
      const prefs = await getUserPreferences();
      setScanLimit(prefs.scan_limit ?? 200);
      setErpUrl(prefs.erp_url || '');
      setErpApiKey(prefs.erp_api_key || '');
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    }
  };

  const handleSaveWebhook = async () => {
    setSavingWebhook(true);
    setWebhookTestResult(null);
    try {
      await saveWebhookConfig({ erp_url: erpUrl, erp_api_key: erpApiKey });
      toast.success('Configuration webhook ERP enregistrée');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleTestWebhook = async () => {
    setTestingWebhook(true);
    setWebhookTestResult(null);
    try {
      await saveWebhookConfig({ erp_url: erpUrl, erp_api_key: erpApiKey });
      const result = await testErpWebhook();
      setWebhookTestResult(result);
      if (result.success) {
        toast.success(`Webhook OK (HTTP ${result.status_code})`);
      } else {
        toast.error(result.error ? `Échec : ${result.error}` : `Échec (HTTP ${result.status_code})`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du test');
    } finally {
      setTestingWebhook(false);
    }
  };

  const handleSavePreferences = async () => {
    setSavingPrefs(true);
    try {
      await updateUserPreferences({ scan_limit: scanLimit });
      toast.success('Préférences enregistrées');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement des préférences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const fetchTokens = async () => {
    try {
      const data = await getApiTokens();
      setTokens(data);
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToken = async (provider) => {
    const token = newTokens[provider];
    if (!token) {
      toast.error('Veuillez entrer un token');
      return;
    }

    setSaving(true);
    try {
      await createApiToken(provider, token);
      toast.success(`Token ${provider === 'backmarket' ? 'Back Market' : 'Pennylane'} enregistré`);
      setNewTokens(prev => ({ ...prev, [provider]: '' }));
      fetchTokens();
    } catch (error) {
      console.error('Failed to save token:', error);
      const message = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteToken = async (provider) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce token ?')) return;

    try {
      await deleteApiToken(provider);
      toast.success('Token supprimé');
      fetchTokens();
    } catch (error) {
      console.error('Failed to delete token:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const isConnected = (provider) => tokens.some(t => t.provider === provider);

  const API_BASE = process.env.NODE_ENV === 'production' ? '' : process.env.REACT_APP_BACKEND_URL;
  const apiDocText = `═══════════════════════════════════════════════════
  API BACK MARKET SELLER - DOCUMENTATION ERP
═══════════════════════════════════════════════════

BASE URL: ${window.location.origin}/api/external

AUTHENTIFICATION:
  Header: X-API-Key: <votre-clé-api>

─────────────────────────────────────────
1. LISTER TOUTES LES FACTURES
─────────────────────────────────────────
GET ${API_BASE}/api/external/invoices

Réponse:
[
  {
    "imei": "353346541286084",
    "invoice_number": "F-2026-03-19",
    "final_price": 370.00,
    "currency": "EUR",
    "customer_name": "Fernanda Domingos",
    "order_id": "78143374",
    "sku": "m661",
    "status": "vendu-bm",
    "created_at": "2026-03-09T17:14:38",
    "erp_synced": false
  }
]

─────────────────────────────────────────
2. DÉTAIL D'UNE FACTURE
─────────────────────────────────────────
GET ${API_BASE}/api/external/invoices/{order_id}

Réponse:
{
  "imei": "353346541286084",
  "invoice_number": "F-2026-03-19",
  "final_price": 370.00,
  "currency": "EUR",
  "customer_name": "Fernanda Domingos",
  "order_id": "78143374",
  "status": "vendu-bm",
  "sku": "m661",
  "erp_synced": false
}

─────────────────────────────────────────
3. EXEMPLE CURL
─────────────────────────────────────────
curl -H "X-API-Key: bms_votre_cle" \\
  ${API_BASE}/api/external/invoices

─────────────────────────────────────────
4. CALLBACK SYNC (ERP -> CE LOGICIEL)
─────────────────────────────────────────
Quand l'ERP traite une facture, il DOIT
renvoyer le résultat via ce endpoint:

POST ${API_BASE}/api/external/sync-status
Header: X-API-Key: bms_votre_cle
Body:
  Succès:
  {"imei":"353346541286084","status":"ok"}

  Erreur (IMEI non trouvé):
  {
    "imei": "353346541286084",
    "status": "error",
    "message": "IMEI non trouvé dans l'ERP"
  }

─────────────────────────────────────────
CLÉ D'ASSOCIATION ERP:
─────────────────────────────────────────
  L'IMEI est la clé unique pour associer
  la facture au produit dans l'ERP.

  imei            → IMEI du produit (clé)
  invoice_number  → Numéro facture Pennylane
  final_price     → Prix de vente final
  status          → "vendu-bm"
  order_id        → Numéro commande BM
  erp_synced      → true si déjà synchronisé`;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Paramètres
          </h1>
          <p className="text-slate-600 mt-1">
            Gérez votre compte et vos intégrations
          </p>
        </div>

        <Tabs defaultValue="integrations" className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="integrations" className="data-[state=active]:bg-white">
              <LinkIcon className="w-4 h-4 mr-2" />
              Intégrations
            </TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:bg-white">
              <User className="w-4 h-4 mr-2" />
              Profil
            </TabsTrigger>
            <TabsTrigger value="preferences" className="data-[state=active]:bg-white">
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Préférences
            </TabsTrigger>
            <TabsTrigger value="api-external" className="data-[state=active]:bg-white">
              <Code className="w-4 h-4 mr-2" />
              API Externe
            </TabsTrigger>
          </TabsList>

          {/* Integrations Tab */}
          <TabsContent value="integrations" className="space-y-6">
            {/* Back Market Integration */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="border-slate-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${isConnected('backmarket') ? 'bg-emerald-500' : 'bg-slate-200'} flex items-center justify-center`}>
                        <Key className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                          Back Market
                        </CardTitle>
                        <CardDescription>
                          API vendeur Back Market
                        </CardDescription>
                      </div>
                    </div>
                    {isConnected('backmarket') ? (
                      <Badge className="bg-emerald-100 text-emerald-800">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Connecté
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-slate-500">
                        <XCircle className="w-3 h-3 mr-1" />
                        Non connecté
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-sm text-slate-700 mb-2">Comment obtenir votre token ?</h4>
                    <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                      <li>Connectez-vous à votre <a href="https://www.backmarket.fr/bo-seller" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">Back Office Back Market</a></li>
                      <li>Allez dans Paramètres {'>'} API</li>
                      <li>Copiez votre token d'authentification</li>
                    </ol>
                  </div>

                  {isConnected('backmarket') ? (
                    <div className="flex items-center justify-between p-4 border border-emerald-200 bg-emerald-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        <span className="text-sm text-emerald-800">Token configuré et actif</span>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteToken('backmarket')}
                        data-testid="delete-backmarket-token-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Supprimer
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>Token API Back Market</Label>
                        <div className="relative">
                          <Input
                            type={showTokens.backmarket ? 'text' : 'password'}
                            placeholder="YjM3NTYwYzU2YTE4ZDk1Ym..."
                            value={newTokens.backmarket}
                            onChange={(e) => setNewTokens(prev => ({ ...prev, backmarket: e.target.value }))}
                            data-testid="backmarket-token-input"
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2"
                            onClick={() => setShowTokens(prev => ({ ...prev, backmarket: !prev.backmarket }))}
                          >
                            {showTokens.backmarket ? (
                              <EyeOff className="w-4 h-4 text-slate-400" />
                            ) : (
                              <Eye className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                        </div>
                      </div>
                      <Button 
                        onClick={() => handleSaveToken('backmarket')}
                        disabled={saving || !newTokens.backmarket}
                        className="bg-emerald-500 hover:bg-emerald-600"
                        data-testid="save-backmarket-token-btn"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4 mr-2" />
                        )}
                        Enregistrer le token
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Pennylane Integration */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Card className="border-slate-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${isConnected('pennylane') ? 'bg-emerald-500' : 'bg-slate-200'} flex items-center justify-center`}>
                        <Key className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                          Pennylane
                        </CardTitle>
                        <CardDescription>
                          Facturation et comptabilité
                        </CardDescription>
                      </div>
                    </div>
                    {isConnected('pennylane') ? (
                      <Badge className="bg-emerald-100 text-emerald-800">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Connecté
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-slate-500">
                        <XCircle className="w-3 h-3 mr-1" />
                        Non connecté
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-sm text-slate-700 mb-2">Comment obtenir votre token ?</h4>
                    <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                      <li>Connectez-vous à votre <a href="https://app.pennylane.com" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">compte Pennylane</a></li>
                      <li>Allez dans Profil {'>'} Environnement de test (recommandé)</li>
                      <li>Générez un token API</li>
                    </ol>
                  </div>

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-blue-800 font-medium">Environnement de test recommandé</p>
                        <p className="text-xs text-blue-700 mt-1">
                          Pennylane recommande de créer un environnement sandbox pour tester l'intégration.
                        </p>
                      </div>
                    </div>
                  </div>

                  {isConnected('pennylane') ? (
                    <div className="flex items-center justify-between p-4 border border-emerald-200 bg-emerald-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        <span className="text-sm text-emerald-800">Token configuré et actif</span>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteToken('pennylane')}
                        data-testid="delete-pennylane-token-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Supprimer
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label>Token API Pennylane</Label>
                        <div className="relative">
                          <Input
                            type={showTokens.pennylane ? 'text' : 'password'}
                            placeholder="GQF174tA8U1imHxdidcJ..."
                            value={newTokens.pennylane}
                            onChange={(e) => setNewTokens(prev => ({ ...prev, pennylane: e.target.value }))}
                            data-testid="pennylane-token-input"
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2"
                            onClick={() => setShowTokens(prev => ({ ...prev, pennylane: !prev.pennylane }))}
                          >
                            {showTokens.pennylane ? (
                              <EyeOff className="w-4 h-4 text-slate-400" />
                            ) : (
                              <Eye className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                        </div>
                      </div>
                      <Button 
                        onClick={() => handleSaveToken('pennylane')}
                        disabled={saving || !newTokens.pennylane}
                        className="bg-emerald-500 hover:bg-emerald-600"
                        data-testid="save-pennylane-token-btn"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4 mr-2" />
                        )}
                        Enregistrer le token
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Informations du compte
                  </CardTitle>
                  <CardDescription>
                    Vos informations d'entreprise
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Building className="w-8 h-8 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xl font-medium text-slate-900">{user?.company_name}</p>
                      <p className="text-slate-500">{user?.email}</p>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-slate-500 text-sm">Nom de l'entreprise</Label>
                      <p className="font-medium text-slate-900 mt-1">{user?.company_name || 'Non renseigné'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-sm">Numéro SIRET</Label>
                      <p className="font-medium text-slate-900 mt-1">{user?.siret || 'Non renseigné'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-sm">Email</Label>
                      <p className="font-medium text-slate-900 mt-1">{user?.email}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-sm">Date d'inscription</Label>
                      <p className="font-medium text-slate-900 mt-1">
                        {user?.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                      </p>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <Label className="text-slate-500 text-sm">Abonnement</Label>
                    <div className="mt-2">
                      {user?.subscription_plan ? (
                        <Badge className="bg-emerald-100 text-emerald-800 capitalize">
                          Plan {user.subscription_plan} - {user.subscription_status}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">
                          Aucun abonnement actif
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Préférences de scan
                  </CardTitle>
                  <CardDescription>
                    Configurez le nombre de commandes analysées pour les filtres "Annulé" et "En attente facture"
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-amber-800 font-medium">Impact sur la vitesse</p>
                        <p className="text-xs text-amber-700 mt-1">
                          Les filtres "Annulé" et "En attente facture" nécessitent un scan des commandes. 
                          Réduire le nombre de commandes scannées accélère considérablement la réponse.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-slate-700">Nombre de commandes à scanner</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { value: 100, label: '100', desc: 'Rapide (~3s)' },
                        { value: 200, label: '200', desc: 'Recommandé (~6s)' },
                        { value: 500, label: '500', desc: 'Étendu (~15s)' },
                        { value: 0, label: 'Toutes', desc: 'Complet (~30s+)' },
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setScanLimit(option.value)}
                          data-testid={`scan-limit-${option.value}`}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${
                            scanLimit === option.value
                              ? 'border-emerald-500 bg-emerald-50'
                              : 'border-slate-200 hover:border-slate-300 bg-white'
                          }`}
                        >
                          <p className={`text-lg font-bold ${scanLimit === option.value ? 'text-emerald-700' : 'text-slate-900'}`}>
                            {option.label}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">{option.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={handleSavePreferences}
                    disabled={savingPrefs}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    data-testid="save-preferences-btn"
                  >
                    {savingPrefs ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Enregistrer les préférences
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* API External Tab */}
          <TabsContent value="api-external">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* API Key Management */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-emerald-600" />
                      Clé API
                    </div>
                  </CardTitle>
                  <CardDescription>
                    Générez une clé pour connecter votre ERP ou tout autre service externe
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {newApiKey && (
                    <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg" data-testid="new-api-key-alert">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-amber-800">Clé générée — copiez-la maintenant !</p>
                          <p className="text-xs text-amber-700 mt-1 mb-2">Cette clé ne sera plus affichée en entier après cette page.</p>
                          <div className="flex items-center gap-2 bg-white p-2 rounded border border-amber-200">
                            <code className="text-xs text-slate-800 flex-1 break-all font-mono" data-testid="api-key-value">{newApiKey}</code>
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(newApiKey)}>
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {apiKeys.length > 0 && (
                    <div className="space-y-2">
                      {apiKeys.map((k) => (
                        <div key={k.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                          <div className="flex items-center gap-3">
                            <Key className="w-4 h-4 text-slate-500" />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-slate-800">{k.name || 'Sans nom'}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${k.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {k.active ? 'Active' : 'Révoquée'}
                                </span>
                              </div>
                              <code className="text-xs text-slate-500 font-mono">{k.key_masked}</code>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            {k.last_used && <span>Utilisée: {new Date(k.last_used).toLocaleDateString('fr-FR')}</span>}
                            {k.active && (
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleRevokeKey(k.id)}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <div className="flex items-center gap-2 flex-1">
                      <Tag className="w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Nom de la clé (ex: ERP, Test, Comptabilité...)"
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        className="flex-1"
                        data-testid="api-key-name-input"
                      />
                    </div>
                    <Button
                      onClick={handleGenerateApiKey}
                      disabled={generatingKey || !newKeyName.trim()}
                      className="bg-emerald-500 hover:bg-emerald-600"
                      data-testid="generate-api-key-btn"
                    >
                      {generatingKey ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                      Créer une clé
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Webhook ERP */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <div className="flex items-center gap-2">
                      <LinkIcon className="w-5 h-5 text-purple-600" />
                      Webhook ERP (sortant)
                    </div>
                  </CardTitle>
                  <CardDescription>
                    RefurbFlow appelle cette URL pour notifier votre ERP : vente Back Market détectée (statut "vendu-bm"), facturation. Auth : header X-API-Key.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">URL du webhook ERP</Label>
                    <Input
                      placeholder="https://votre-erp.com/api/webhooks/refurbflow"
                      value={erpUrl}
                      onChange={(e) => setErpUrl(e.target.value)}
                      data-testid="erp-webhook-url-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Clé API envoyée (header X-API-Key)</Label>
                    <Input
                      placeholder="Laisser vide pour utiliser votre clé API RefurbFlow"
                      value={erpApiKey}
                      onChange={(e) => setErpApiKey(e.target.value)}
                      data-testid="erp-webhook-key-input"
                    />
                    <p className="text-xs text-slate-500">Si votre ERP attend une clé différente, renseignez-la ici. Sinon, la clé API RefurbFlow active est utilisée.</p>
                  </div>
                  {webhookTestResult && (
                    <div data-testid="webhook-test-result" className={`p-3 rounded-lg border text-sm ${webhookTestResult.success ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
                      {webhookTestResult.success
                        ? `✓ Webhook joignable — HTTP ${webhookTestResult.status_code}`
                        : `✗ Échec — ${webhookTestResult.error || `HTTP ${webhookTestResult.status_code} : ${webhookTestResult.body || ''}`}`}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveWebhook}
                      disabled={savingWebhook}
                      className="bg-emerald-500 hover:bg-emerald-600"
                      data-testid="save-webhook-btn"
                    >
                      {savingWebhook ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Enregistrer
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleTestWebhook}
                      disabled={testingWebhook || !erpUrl.trim()}
                      data-testid="test-webhook-btn"
                    >
                      {testingWebhook ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Tester le webhook
                    </Button>
                  </div>
                </CardContent>
              </Card>


              {/* API Documentation */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <div className="flex items-center gap-2">
                      <Code className="w-5 h-5 text-blue-600" />
                      Documentation API — Instructions ERP
                    </div>
                  </CardTitle>
                  <CardDescription>
                    Transmettez ces instructions à votre développeur ERP
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="relative">
                    <Button
                      variant="outline"
                      size="sm"
                      className="absolute top-2 right-2 z-10"
                      onClick={() => copyToClipboard(apiDocText)}
                    >
                      <Copy className="w-3.5 h-3.5 mr-1" /> Copier
                    </Button>
                    <pre className="p-4 bg-slate-900 text-slate-100 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed" data-testid="api-doc">
{apiDocText}
                    </pre>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
